import React, { useContext } from 'react';
import { useLocalStorage } from '@uidotdev/usehooks';
import {
  ModelVersionEventType,
  supportedEventTypes,
  supportedRegions,
  supportedRegionsWithNameValue,
} from 'src/context/apiState';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { useTriggerWorkflow } from 'src/hooks/useTriggerWorkflow';
import { ApiContext } from 'src/context/apiContext';
import {
  AttributeEditor,
  Box,
  Button,
  FormField,
  Input,
  Modal,
  Select,
  SpaceBetween,
} from '@cloudscape-design/components';
import { PermissionAwareButton } from 'src/components/PermissionAwareButton';

export const TriggerWorkflowModal = ({
  values,
  onClose,
}: {
  values?: {
    startingOperatorType: string;
    startingOperatorName: string;
    variables: { key: string; value: string }[];
    region: string;
  };
  onClose: () => void;
}) => {
  const workflowTriggerValidationSchema = yup.object().shape({
    startingOperatorType: yup
      .string()
      .oneOf(
        supportedEventTypes.map((item) => item.value),
        'Please select the starting operator type.',
      )
      .required("Please select the starting operator's type."),

    startingOperatorName: yup
      .string()
      .required("Please enter the starting operator's name."),

    variables: yup.array().of(
      yup.object().shape({
        key: yup.string().required('Please enter a valid key.'),
        value: yup.string().required('Please enter a valid value.'),
      }),
    ),

    region: yup
      .string()
      .oneOf(
        supportedRegions.map((item) => item),
        'Please select a region.',
      )
      .required('Please select a region.'),
  });

  const initialValues: {
    startingOperatorType: ModelVersionEventType;
    startingOperatorName: string;
    variables: { key: string; value: string }[];
    region: string;
  } = {
    startingOperatorType: ModelVersionEventType.RETRAINING,
    startingOperatorName: values?.startingOperatorName || '',
    variables: [],
    region: 'us-east-1',
  };

  const { state } = useContext(ApiContext);
  const [triggerConfig, setTriggerConfig] = useLocalStorage(
    `triggerWorkflowConfig-account-${state.search.curAccountNo}-mv-${state.search.curModelVersionId}`,
    initialValues,
  );
  const [
    eventApiLoading,
    eventApiError,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    eventApiResponse,
    callTriggerWorkflowApi,
  ] = useTriggerWorkflow();

  const formik = useFormik({
    initialValues: triggerConfig,
    onSubmit: async (values) => {
      setTriggerConfig(values);
      await callTriggerWorkflowApi(
        {
          startingOperatorType: values.startingOperatorType,
          startingOperatorName: values.startingOperatorName,
          modelVersionId: state.search.curModelVersionId,
          region: values.region,
          variables: values.variables,
        },
        onClose,
      );
    },
    validationSchema: workflowTriggerValidationSchema,
  });

  return (
    <Modal
      visible={true}
      onDismiss={() => {
        onClose();
      }}
      header="Workflow Triggering"
      footer={
        <Box float="right">
          <SpaceBetween size={'m'} direction={'horizontal'}>
            <Button variant="link" onClick={onClose}>
              Cancel
            </Button>
            <PermissionAwareButton
              loading={eventApiLoading}
              onClick={() => {
                formik.handleSubmit();
              }}
              requiredPermission="READ_AND_LIMITED_ACTION"
              actionDescription="trigger workflows"
            >
              Submit
            </PermissionAwareButton>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size={'m'}>
        <p className="mb-2">
          Triggers a workflow that matches the following information
        </p>
        <form onSubmit={formik.handleSubmit}>
          <SpaceBetween size={'m'}>
            <FormField label="Starting Operator Type">
              <Select
                selectedOption={{
                  label: supportedEventTypes.find(
                    (item) => item.value === formik.values.startingOperatorType,
                  )?.name,
                  value: formik.values.startingOperatorType,
                }}
                onChange={(e) => {
                  formik.setFieldValue(
                    'startingOperatorType',
                    e.detail.selectedOption.value,
                  );
                }}
                errorText={formik.errors.startingOperatorType}
                options={supportedEventTypes.map((eventType) => {
                  return {
                    label: eventType.name,
                    value: eventType.value,
                  };
                })}
                placeholder="Select the starting operator's type"
              />
            </FormField>

            <FormField
              label="Starting Operator Name"
              errorText={formik.errors.startingOperatorName}
            >
              <Input
                value={formik.values.startingOperatorName}
                placeholder="my-operator-name"
                name="startingOperatorName"
                onChange={(e) => {
                  formik.setFieldValue('startingOperatorName', e.detail.value);
                }}
              />
            </FormField>

            <FormField label="Region">
              <Select
                selectedOption={{
                  label: formik.values.region,
                  value: formik.values.region,
                }}
                options={supportedRegionsWithNameValue}
                placeholder="Select a region"
                onChange={(e) => {
                  formik.setFieldValue('region', e.detail.selectedOption.value);
                }}
                errorText={formik.errors.region}
              />
            </FormField>

            <FormField
              label="Variables"
              errorText={
                formik.touched.variables && formik.errors.variables
                  ? 'Please check variable settings'
                  : undefined
              }
            >
              <AttributeEditor
                onAddButtonClick={() =>
                  formik.setFieldValue('variables', [
                    ...formik.values.variables,
                    {},
                  ])
                }
                onRemoveButtonClick={({ detail: { itemIndex } }) => {
                  const tmpItems = [...formik.values.variables];
                  tmpItems.splice(itemIndex, 1);
                  formik.setFieldValue('variables', tmpItems);
                }}
                items={formik.values.variables}
                addButtonText="Add Variables"
                removeButtonText="Remove"
                definition={[
                  {
                    label: 'Key',
                    control: (item, itemIndex) => (
                      <Input
                        value={item.key}
                        placeholder="variable key"
                        invalid={
                          // @ts-expect-error
                          !!formik.errors.variables?.[itemIndex]?.key
                        }
                        onChange={(e) => {
                          const tmpItems = [...formik.values.variables];
                          tmpItems[itemIndex] = {
                            key: e.detail.value,
                            value: item.value,
                          };
                          formik.setFieldValue('variables', tmpItems);
                        }}
                      />
                    ),
                  },
                  {
                    label: 'Value',
                    control: (item, itemIndex) => (
                      <Input
                        value={item.value}
                        placeholder="variable value"
                        invalid={
                          // @ts-expect-error
                          !!formik.errors.variables?.[itemIndex]?.value
                        }
                        onChange={(e) => {
                          const tmpItems = [...formik.values.variables];
                          tmpItems[itemIndex] = {
                            key: item.key,
                            value: e.detail.value,
                          };
                          formik.setFieldValue('variables', tmpItems);
                        }}
                      />
                    ),
                  },
                ]}
                empty="No variables set."
              />
            </FormField>
          </SpaceBetween>
        </form>
      </SpaceBetween>
      {eventApiError && <p className="error-text">{eventApiError.message}</p>}
    </Modal>
  );
};
